<template>
  <section class="py-20 px-6 md:px-12 bg-gray-50">
    <div class="max-w-7xl mx-auto">
      <h2 class="text-4xl font-extrabold text-center mb-4 text-gray-900">
        {{ $t('home.caseStudies.title') }}
      </h2>
      <p class="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
        {{ $t('home.caseStudies.subtitle') }}
      </p>
      
      <!-- 行业筛选标签 -->
      <div class="flex flex-wrap justify-center gap-3 mb-12">
        <button
          @click="selectedIndustry = null"
          class="px-4 py-2 rounded-full text-sm font-medium transition duration-300"
          :class="selectedIndustry === null 
            ? 'bg-purple-600 text-white' 
            : 'bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600'"
        >
          {{ $t('home.caseStudies.allIndustries') }}
        </button>
        <button
          v-for="industry in industries"
          :key="industry"
          @click="selectedIndustry = industry"
          class="px-4 py-2 rounded-full text-sm font-medium transition duration-300"
          :class="selectedIndustry === industry 
            ? 'bg-purple-600 text-white' 
            : 'bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600'"
        >
          {{ $t(`home.caseStudies.${industry}`) }}
        </button>
      </div>
      
      <!-- 案例卡片网格 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <router-link
          v-for="caseStudy in filteredCaseStudies"
          :key="caseStudy.id"
          :to="{ path: '/case-studies', query: { industry: caseStudy.industry, region: getRegionValue(caseStudy.region) } }"
          class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition duration-300 transform hover:-translate-y-2 block cursor-pointer"
        >
          <!-- 案例缩略图 -->
          <div class="relative h-48 overflow-hidden">
            <img 
              :src="caseStudy.thumbnail" 
              :alt="getLocalizedText(caseStudy.title)"
              class="w-full h-full object-cover"
            />
            <!-- 渐变遮罩 -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <!-- 行业标签 -->
            <div class="absolute top-4 left-4">
              <span class="inline-block px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full shadow-lg">
                {{ $t(`home.caseStudies.${caseStudy.industry}`) }}
              </span>
            </div>
            <!-- 地区标签 -->
            <div class="absolute top-4 right-4">
              <span class="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full shadow-lg">
                {{ caseStudy.region }}
              </span>
            </div>
          </div>
          
          <!-- 案例内容 -->
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
              {{ getLocalizedText(caseStudy.title) }}
            </h3>
            
            <p class="text-gray-600 text-sm mb-4 line-clamp-3">
              {{ getLocalizedText(caseStudy.summary) }}
            </p>
            
            <!-- 项目成果 -->
            <div class="space-y-2 mb-4">
              <div
                v-for="outcome in caseStudy.outcomes.slice(0, 3)"
                :key="outcome.metric"
                class="flex items-center text-sm"
              >
                <i :class="['fas', outcome.icon, 'text-green-500 mr-2']"></i>
                <span class="text-gray-700">
                  {{ getLocalizedText(outcome.label) }}: 
                  <span class="font-semibold text-purple-600">{{ outcome.improvement }}</span>
                </span>
              </div>
            </div>
            
            <!-- 查看详情按钮 -->
            <div class="mt-4 flex justify-end">
              <span class="inline-flex items-center text-purple-600 hover:text-purple-700 font-semibold text-sm">
                {{ $t('home.caseStudies.viewDetail') }}
                <i class="fas fa-arrow-right ml-2"></i>
              </span>
            </div>
          </div>
        </router-link>
      </div>
      
      <!-- 无结果提示 -->
      <div v-if="filteredCaseStudies.length === 0" class="text-center py-12">
        <i class="fas fa-search text-6xl text-gray-300 mb-4"></i>
        <p class="text-gray-500 text-lg">{{ noResultsMessage }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useI18nHelpers } from '@/composables/useI18nHelpers'
import { featuredCaseStudies } from '@/data/caseStudies'

const { t } = useI18n()
const { getLocalizedText } = useI18nHelpers()

const selectedIndustry = ref(null)
const industries = ['finance', 'healthcare', 'manufacturing', 'government', 'retail', 'professional', 'education']

// 区域代码映射
const regionMap = {
  'JP': 'japan',
  'SG': 'singapore',
  'KR': 'korea',
  'CN': 'china',
  'HK': 'hongkong',
  'TW': 'taiwan',
  'US': 'america',
  'EU': 'europe'
}

// 获取区域值
const getRegionValue = (region) => {
  return regionMap[region] || region.toLowerCase()
}

// 过滤后的案例
const filteredCaseStudies = computed(() => {
  if (!selectedIndustry.value) {
    return featuredCaseStudies
  }
  return featuredCaseStudies.filter(cs => cs.industry === selectedIndustry.value)
})

// 无结果消息
const noResultsMessage = computed(() => {
  if (selectedIndustry.value) {
    return t('home.caseStudies.noResults', {
      industry: t(`home.caseStudies.${selectedIndustry.value}`)
    })
  }
  return t('home.caseStudies.noCases')
})
</script>

<style scoped>
/* 文本截断 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
